import { selectedSiteId } from '@/lib/siteDashboard';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { Location } from '@/lib/api';
import styles from './SitePages.module.css';

/**
 * The site picker the framework asks for: "If they hold several sites,
 * a picker."
 *
 * `AccessAssignment.location_ids` has always been an array and every
 * scoped route honours all of it, but nothing in the UI let a holder of
 * two buildings choose which one they were looking at — so a property
 * manager with a portfolio saw their sites summed and could not act on
 * one. The picker renders only when there are two or more, matching
 * ScopedReportsPage's `locationFilter='multi-only'` behaviour: a
 * single-site admin should not be shown a one-option dropdown.
 */
export function SitePicker({
  sites, selectedId, onSelect,
}: {
  sites: ReadonlyArray<Location>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}): JSX.Element | null {
  if (sites.length < 2) return null;
  return (
    <label className={styles.picker}>
      <span className={styles.pickerLabel}>Site</span>
      <select
        className={styles.select}
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value)}
      >
        {sites.map((s) => (
          <option key={s._id} value={s._id}>{s.name}</option>
        ))}
      </select>
    </label>
  );
}

/**
 * Selection state for the picker, shared by the three Site pages so a
 * choice made on one is the same shape on the others.
 *
 * Defaults to the first site once the list resolves, and re-defaults if
 * the current selection disappears (an operator un-assigns a site while
 * the tab is open) rather than rendering an empty page.
 */
export function useSelectedSite(sites: ReadonlyArray<Location>): {
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  selected: Location | null;
} {
  const [selectedId, setSelectedId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('site'));
  useEffect(() => {
    if (sites.length === 0) return;
    const next = selectedSiteId(sites.map((s) => s._id), selectedId);
    if (next !== selectedId) setSelectedId(next);
  }, [sites, selectedId]);
  useEffect(() => {
    const onPopState = () => setSelectedId(new URLSearchParams(window.location.search).get('site'));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const selectSite = (id: string) => {
    setSelectedId(id);
    const url = new URL(window.location.href);
    url.searchParams.set('site', id);
    window.history.replaceState({}, '', url);
  };
  const selected = sites.find((s) => s._id === selectedId) ?? null;
  return { selectedId, setSelectedId: selectSite, selected };
}
